import {
  ITableDataQuadroSocietario,
  ITableDataQuadroSocietarioEmpresa
} from "@/features/contabil/dashboard/widgets/QuadroSocietario/types";
import type {
  IQuadroSocietarioResponse,
  IQuadroSocietarioMatrizesResponse,
  IQuadroSocietarioMatrizesDeclaradoResponse
} from "../requests/types";
import { groupBy } from "@/core/common/utils";
import {
  RegimeTributarioDescription,
  RegimeTributarioEnum
} from "@/features/contabil/@types/types";
import EmpresaMapper from "@/features/contabil/common/EmpresaMapperAdapter";
import Empresa from "@/features/contabil/common/Empresa";

export interface IQuadroSocietarioMatrizesSocio {
  percentual: number;
  name: string;
  id: string;
}

export interface IQuadroSocietarioMatrizes {
  socios: IQuadroSocietarioMatrizesSocio[];
  empresa: Empresa;
}

export interface IQuadroSocietario {
  matrizes: IQuadroSocietarioMatrizes[];
}

interface ISociosPorEmpresa extends IQuadroSocietarioMatrizesDeclaradoResponse {
  cnpj: string | null;
  nomeFantasia: string | null;
  regime: RegimeTributarioEnum | null;
  updatedAt: string | null;
  aguardandoProcessamento: boolean;
}

export default class QuadroSocietario {
  constructor(readonly response: IQuadroSocietarioResponse | undefined) {}

  private handleMatriz(
    matriz: IQuadroSocietarioMatrizesResponse
  ): IQuadroSocietarioMatrizes {
    return {
      socios:
        matriz.declarado?.map((declarado) => ({
          name: declarado.name || "-",
          percentual: declarado.percentual || 0,
          id: declarado.id || ""
        })) || [],
      empresa: EmpresaMapper.toEntity(matriz)
    };
  }

  private handleEmpresas(): ISociosPorEmpresa[] {
    return (
      this.response?.matrizes
        ?.map((matriz) => {
          if (!matriz.declarado)
            return [
              {
                cadastralSituation: null,
                country: null,
                id: null,
                name: null,
                percentual: null,
                personQualification: null,
                qualification: null,
                taxableIncome: 0,
                taxFreeIncome: 0,
                votingCapital: null,
                withholdingTax: 0,
                cnpj: matriz.cnpj.completo,
                nomeFantasia: matriz.nomeFantasia,
                regime: matriz.regimeTributario,
                updatedAt: matriz.updatedAt,
                aguardandoProcessamento: false
              }
            ];

          return (
            matriz.declarado?.map((socio) => ({
              ...socio,
              cnpj: matriz.cnpj.completo,
              nomeFantasia: matriz.nomeFantasia,
              regime: matriz.regimeTributario,
              updatedAt: matriz.updatedAt,
              aguardandoProcessamento: matriz.updatedAt === null
            })) || []
          );
        })
        .flat() || []
    );
  }

  empresasGrouped() {
    const empresas = groupBy(this.handleEmpresas(), (empresa) => empresa.cnpj);

    return empresas;
  }

  private naoPossuiValoresDeclarados(
    empresa: IQuadroSocietarioMatrizesResponse
  ) {
    return empresa.declarado === null || empresa.declarado.length === 0;
  }

  private socioPossuiPercentualDeclarado(percentualSocios: (number | null)[]) {
    return percentualSocios.every(
      (percentual) => Number(percentual) === 0 || percentual === null
    );
  }

  empresaEstaPendentesDeclaracao(cnpj: string) {
    const empresa = this.response?.matrizes?.find(
      (empresa) => empresa.cnpj.completo == cnpj
    );

    if (!empresa) return false;

    if (this.naoPossuiValoresDeclarados(empresa)) return true;

    const percentualSocios = empresa.declarado?.map(
      (socio) => socio.percentual
    );

    return this.socioPossuiPercentualDeclarado(percentualSocios || []);
  }

  empresasPendentesDeclaracao() {
    return (
      this.response?.matrizes?.some((empresa) => {
        if (this.naoPossuiValoresDeclarados(empresa)) return true;

        const percentualSocios = empresa.declarado?.map(
          (socio) => socio.percentual
        );

        return this.socioPossuiPercentualDeclarado(percentualSocios || []);
      }) || false
    );
  }

  isQuadroNaoObrigatorio() {
    return (
      this.response?.matrizes?.every(
        (empresa) => empresa.quadroSocietarioNaoObrigatorio || false
      ) || false
    );
  }

  isQuadroNaoObrigatorioById(cnpj: string) {
    return (
      this.response?.matrizes?.find((empresa) => empresa.cnpj.completo === cnpj)
        ?.quadroSocietarioNaoObrigatorio || false
    );
  }

  sociosGrouped() {
    const empresas = this.handleEmpresas();
    //realizar esse filtro, porque quando está na visualização de loja, removo a coluna da tabela
    const empresasQuePossuemSocios = empresas.filter((socio) => socio.id);

    const socios = groupBy(empresasQuePossuemSocios, (empresa) => empresa.id);

    return socios;
  }

  getParticipacaoByEmpresa(id: string, cnpj: string) {
    const socio = this.handleEmpresas().find(
      (socio) => socio?.cnpj === cnpj && socio?.id === id
    );

    if (!socio?.id) return "-";

    const result = socio?.percentual || 0;

    return `${result}%`;
  }

  getSocioById(id: string, empresa: string) {
    const socio = this.handleEmpresas().find(
      (socio) => socio.id === id && socio.cnpj === empresa
    );

    return socio;
  }

  getSocioNameById(id: string) {
    const socio = this.handleEmpresas().find((socio) => socio.id === id);

    return socio;
  }

  toTableEmpresas(): ITableDataQuadroSocietarioEmpresa[] {
    const sociosAgrupados = Object.values(
      groupBy(this.handleEmpresas(), (empresa) => empresa.cnpj)
    );

    // sempre só o primeiro sócio porque só preciso saber o nome da empresa enão as informações do sócio estou ignorando
    const empresaNaoDuplicadas = sociosAgrupados.map((socio) =>
      socio.find((socio) => socio.nomeFantasia)
    );

    return empresaNaoDuplicadas.map((empresa) => ({
      ano: "-",
      empresa: empresa?.nomeFantasia || "-",
      regime:
        RegimeTributarioDescription.get(empresa?.regime || 0)?.toString() ||
        "-",
      socio: empresa?.id || "",
      cnpj: empresa?.cnpj || "",
      id: empresa?.id || "",
      aguardandoProcessamento: empresa?.aguardandoProcessamento || false
    }));
  }

  toTable(): ITableDataQuadroSocietario[] {
    const empresasAgrupadasPorSocio = Object.values(
      groupBy(this.handleEmpresas(), (socio) => socio.id)
    );

    // o filter é utilizado para não exibir o sócio sem nome na lista de empresas
    const empresasNaoDuplicadas = empresasAgrupadasPorSocio
      .map((socio) => socio.find((socio) => socio.name))
      .filter((socio) => socio?.name);

    return empresasNaoDuplicadas.map((socio) => ({
      ano: "-",
      nome: socio?.name || "-",
      pais: socio?.country || "-",
      participacao: socio?.percentual || 0,
      qualificacao: socio?.personQualification || "-",
      cnpj: socio?.cnpj || "-",
      nomeFantasia: socio?.nomeFantasia || "-",
      id: socio?.id || "-",
      dataAtualizacao: socio?.updatedAt || null,
      aguardandoProcessamento: socio?.aguardandoProcessamento || false
    }));
  }

  handler(): IQuadroSocietario {
    return {
      matrizes:
        this.response?.matrizes?.map((matriz) => this.handleMatriz(matriz)) ||
        []
    };
  }
}
